import { AnalyticsContext, useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags/featureFlags.provider';
import { useEventInfo } from '@shared/utils/eventInfo';
import React, { createContext, useContext, useMemo } from 'react';
import type { ThemeJson } from '../routes/CardCustomizer/CardCustomizer.types';
import { usePrimaryEventLocation } from '../routes/Dashboard/components/ProductDetailsPage/usePrimaryEventLocation';
import { fillPageWithEventInfo } from '../utils/fillPageWithEventInfo';
import { useGetEventPrivacyForCardCustomizerQuery } from '../routes/CardCustomizer/CardCustomizer.graphql.generated';
import { useEventPasswordValue } from '@shared/hooks/useRevealEventPassword';
import { useMainEventImages } from '@shared/utils/eventPhoto';
import { autofillPhotos as applyPhotos } from '../utils/injectPhotosIntoTemplate';

export type TemplateForAutofill = { id: string; themeId?: string; filledAt?: string; themeJSON?: ThemeJson };

// Default behavior is for public shop where there is no associated event.
const AutofillContext = createContext({
  autofill: (_template: TemplateForAutofill, _trackEvent?: 'trackEvent') => {},
  autofillPhotos: (_template: TemplateForAutofill) => {},
  loading: false,
  photosLoading: false,
});

export function AutofillProvider(props: { children: React.ReactNode }) {
  const { children } = props;

  const { track } = useContext(AnalyticsContext);
  const { t } = useTranslation('stationery');
  const tNumerals = t('numerals');

  const { eventHandle, eventInfo, loading: eventInfoLoading } = useEventInfo();
  const { location, loading: locationLoading } = usePrimaryEventLocation();
  const { data: privacyData, loading: privacyDataLoading } = useGetEventPrivacyForCardCustomizerQuery({
    variables: {
      eventId: eventInfo?.eventId ?? '',
    },
    batchMode: 'fast',
    skip: !eventInfo?.eventId,
  });
  const [photosAutofillEnabled] = useIsFeatureEnabled('printAutofillPhotos');
  const { images, loading: photosLoading } = useMainEventImages(10, !photosAutofillEnabled);
  const privacyMode = privacyData?.eventById?.settings.privacyMode;
  const {
    password: eventPassword,
    isResolved: isPasswordResolved,
    error: eventPasswordError,
  } = useEventPasswordValue(eventInfo?.eventId, eventInfo?.hasEventPassword, {
    // privacyMode is a separate query from hasEventPassword, so guard against its still-loading undefined.
    enabled: !!privacyMode && privacyMode !== 'public',
  });
  // A failed reveal must not hold these previews on a skeleton forever; on failure we pass null below
  // so fillPageWithEventInfo leaves the ‹Password› token literal instead of persisting a blank value.
  const isPasswordPending = !isPasswordResolved && !eventPasswordError;
  const loading = eventInfoLoading || locationLoading || privacyDataLoading || isPasswordPending;

  const value = useMemo(() => {
    return {
      autofill: (template: TemplateForAutofill, trackEvent?: 'trackEvent') => {
        if (!template?.themeJSON) return;
        if (!privacyMode) return;
        if (loading) return;

        const { front, back } = template.themeJSON.card;
        const passwordForFill = eventPasswordError ? null : (eventPassword ?? '');
        // prettier-ignore
        const filledPatterns = [
          ...fillPageWithEventInfo(front, eventInfo, location, tNumerals, privacyMode, passwordForFill),
          ...fillPageWithEventInfo(back, eventInfo, location, tNumerals, privacyMode, passwordForFill)
        ];
        template.themeJSON.filledAt = new Date().toISOString();
        if (trackEvent === 'trackEvent') {
          track({
            category: 'messaging.card',
            action: 'Patterns Filled',
            extraInfo: {
              templateId: template.id,
              eventHandle,
              eventId: eventInfo?.eventId,
              filledPatterns,
            },
          });
        }
      },
      autofillPhotos: (template: TemplateForAutofill) => {
        if (!photosAutofillEnabled) return;
        if (photosLoading) return;
        if (template.themeId?.startsWith('upload_your_')) return;
        applyPhotos(template, images);
      },
      loading,
      photosLoading,
    };
  }, [eventHandle, eventInfo, location, loading, tNumerals, track, privacyMode, eventPassword, eventPasswordError, images, photosLoading, photosAutofillEnabled]);

  return <AutofillContext.Provider value={value}>{children}</AutofillContext.Provider>;
}

export function useAutofill() {
  return useContext(AutofillContext);
}
